import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { distinctUntilChanged, map, startWith } from 'rxjs';

import { TenantBootstrapService } from './tenant-bootstrap.service';
import { TenantConfig, TenantPageVisibility } from './tenant.model';

export type TenantPublicPage = keyof TenantPageVisibility;

export const DEFAULT_TENANT_PAGE_VISIBILITY: TenantPageVisibility = {
  league: true,
  tournament: true,
  rules_regulations: true,
  about_us: true,
};

function pageVisibilityForTenant(tenant: TenantConfig | null): TenantPageVisibility {
  return {
    league: tenant?.page_visibility?.league !== false,
    tournament: tenant?.page_visibility?.tournament !== false,
    rules_regulations: tenant?.page_visibility?.rules_regulations !== false,
    about_us: tenant?.page_visibility?.about_us !== false,
  };
}

@Injectable({ providedIn: 'root' })
export class TenantPageVisibilityService {
  readonly pageVisibility$ = this.tenantBootstrap.tenant$.pipe(
    startWith(this.tenantBootstrap.currentTenant),
    map((tenant) => pageVisibilityForTenant(tenant)),
    distinctUntilChanged((previous, current) =>
      Object.keys(DEFAULT_TENANT_PAGE_VISIBILITY).every((page) =>
        previous[page as TenantPublicPage] === current[page as TenantPublicPage]
      )
    )
  );

  constructor(private tenantBootstrap: TenantBootstrapService) {}

  isVisible(page: TenantPublicPage): boolean {
    return pageVisibilityForTenant(this.tenantBootstrap.currentTenant)[page];
  }
}

@Injectable({ providedIn: 'root' })
export class TenantPageVisibilityGuard implements CanActivate {
  constructor(
    private pageVisibility: TenantPageVisibilityService,
    private router: Router,
  ) {}

  canActivate(route: ActivatedRouteSnapshot): boolean | UrlTree {
    const page = route.data?.['tenantPage'] as TenantPublicPage | undefined;
    return !page || this.pageVisibility.isVisible(page)
      ? true
      : this.router.createUrlTree(['/']);
  }
}
